import { TId } from '@apia/util';
import { identifiers, parseTOKS } from './common';
import { ConstantsObject } from './ConstantsObject';

export const constantsES: ConstantsObject = Object.freeze({
  ...identifiers,
  deliverables: [
    {
      title: 'Carta de Proyecto',
      description:
        'Un documento que describe el propósito, alcance, objetivos, partes interesadas y recursos para el proyecto BPM, sirviendo como una autorización formal.',
    },
    {
      title: 'Documentación del Proceso Actual',
      description:
        'Documentación detallada del estado actual de los procesos empresariales, incluyendo diagramas de flujo, descripciones y métricas, proporcionando una base para la mejora.',
    },
    {
      title: 'Informe de Análisis de Brechas',
      description:
        'Un análisis que identifica las brechas entre el estado actual y el estado futuro deseado de los procesos empresariales, destacando ineficiencias y áreas para mejorar.',
    },
    {
      title: 'Diseño del Proceso Futuro',
      description:
        'Un diseño detallado del estado futuro de los procesos empresariales, incluyendo diagramas de flujo de procesos rediseñados y métricas de rendimiento esperadas, destinado a abordar las brechas identificadas.',
    },
    {
      title: 'Caso de Negocio',
      description:
        'Un documento que justifica la inversión en el proyecto BPM, detallando los beneficios esperados, costos y retorno de la inversión.',
    },
    {
      title: 'Plan de Implementación',
      description:
        'Un plan integral para ejecutar el proyecto BPM, incluyendo cronogramas, recursos, roles y responsabilidades, y hitos.',
    },
    {
      title: 'Plan de Gestión del Cambio',
      description:
        'Una estrategia para gestionar el cambio organizacional asociado con el proyecto BPM, incluyendo planes de comunicación y programas de capacitación.',
    },
    {
      title: 'Documento de Requisitos Tecnológicos',
      description:
        'Un documento que describe los requisitos técnicos para apoyar los procesos rediseñados, incluyendo software, hardware e integraciones.',
    },
    {
      title: 'Plan de Pruebas y Casos de Prueba',
      description:
        'Un plan para probar los procesos rediseñados, incluyendo escenarios de prueba, resultados esperados y criterios de éxito.',
    },
    {
      title: 'Materiales de Capacitación',
      description:
        'Materiales desarrollados para capacitar al personal en los nuevos procesos, incluyendo manuales, guías y módulos de aprendizaje en línea.',
    },
    {
      title: 'Informes de Estado del Proyecto',
      description:
        'Actualizaciones regulares proporcionadas a las partes interesadas sobre el progreso del proyecto BPM, incluyendo logros, problemas y próximos pasos.',
    },
    {
      title: 'Informe de Revisión Post-Implementación',
      description:
        'Un documento que evalúa el éxito del proyecto BPM después de su implementación, incluyendo los beneficios alcanzados, métricas de rendimiento y lecciones aprendidas.',
    },
  ],
  errors: {
    cannotImportConfigurationFile:
      'No se puede procesar el archivo dado como configuración del proyecto. ¿Es un archivo de configuración válido?',
    cannotDownloadZipFile:
      'Hubo un error al descargar el archivo de exportación del proyecto.',
    cannotImportObjectsFromApia:
      'Hubo un error al importar elementos desde Apia',
    cannotOpenCollectionSelector:
      'Algo salió mal al abrir el selector de colecciones',
    cannotProcessZipFile: `Hubo un error al procesar la documentación en zip, descargando la versión predeterminada.`,
    cannotRemoveAttributeDueToForeignKey: (entitiesList: string[]) =>
      parseTOKS(
        `No se puede eliminar la entidad actual porque se está utilizando en las claves foráneas de las siguientes entidades: <ul><TOK1></ul>`,
        entitiesList.map((c) => `<li>${c}</li>`).join(''),
      ),
    cannotRemoveInstanceDueToDeps: (entityTitle: string, entityName: string) =>
      parseTOKS(
        `No se puede eliminar la instancia porque se está utilizando en las siguientes instancias de entidades: <TOK1> (<TOK2>)`,
        entityTitle,
        entityName,
      ),
    cannotUpdateAttributeDueToForeignKey: (entitiesList: string[]) =>
      parseTOKS(
        `No se puede actualizar la entidad actual porque se está utilizando en las claves foráneas de las siguientes entidades: <ul><TOK1></ul>`,
        entitiesList.map((c) => `<li>${c}</li>`).join(''),
      ),
    elementAlreadyExists: () => 'El nombre ingresado ya está en uso',
  },
  explanations: {
    docPricingLegend:
      'Este documento ofrece un desglose exhaustivo y fácil de entender de los costes del proyecto, detallando meticulosamente los objetos que se están diseñando para su posterior desarrollo, las distintas funciones del equipo, las actividades principales y los plazos para garantizar una planificación financiera transparente y eficaz.',
    excelActivitiesLegend:
      'A continuación, se muestra la distribución de actividades y roles. \n\nEsta información, utilizada en conjunto con el cronograma de actividades, ayuda a determinar la asignación de asistentes a lo largo de todo el proyecto.',
    excelItemsLegend: [
      'A continuación, se muestra la lista completa de los ítems considerados de interés para el cliente.',
      'El documento enviado con la especificación de procesos y objetos tiene como objetivo explicar la situación, además de buenas prácticas y conceptos generales.',
      'En particular, los indicadores, los documentos (gestión documental) y diversos casos de uso no están incluidos en el alcance y esfuerzo de la propuesta.',
    ],
    excelPricingLegend:
      'Esta hoja de Excel posee un desglose exhaustivo de los costes del proyecto. Se detalla los objetos de desarrollo, las diversas funciones del equipo, las principales actividades y los plazos para garantizar una planificación financiera transparente y eficaz.',
    excelScheduleLegend:
      'A continuación, se muestra la cronología para cada uno de los ítems de interés. Cada columna representa un período de 5 días laborables.',
    elementsWithSimilarities:
      'Estos botones representan elementos que contienen objetos similares dentro de la estructura del proyecto. Al hacer clic en cualquiera de ellos se lanzará un diálogo de fusión, para facilitar la combinación de sus contenidos.',
    generateForm:
      'Ingrese el contenido del formulario: nombre del formulario, título del formulario, campos, tipos de campos, etc.',
    generateEntity:
      'Ingrese el contenido de la entidad: nombre de la entidad, título de la entidad, campos, tipos de campos, etc.',
    generateProcess:
      'Ingrese el contenido del proceso: nombre del proceso, título del proceso, títulos de tareas, nombres de tareas, títulos de formularios, nombres de formularios, títulos de campos, nombres, tipos, etc.',
    naming:
      'Esta funcionalidad permite determinar cómo se asignarán nombres a diferentes elementos del árbol. Se asignarán prefijos a todos los elementos. Los sufijos solo se agregarán a los campos de formulario, donde se evaluará el tipo de campo y el tipo de atributo para generar una composición de ellos: {PREFIX}_{FIELDNAME}_{ATTTYPE}_{FIELDTYPE}_[{GRID}?]',
    panelAutogenerated:
      'Este panel fue autogenerado al crear su widget. Si desea, consulte la definición del widget.',
    searchSimilaritiesModal:
      'Por favor, seleccione una colección para buscar similitudes y especifique el umbral de similitud deseado. El umbral representa la distancia máxima permitida entre dos elementos para considerarlos similares.',
    templates:
      'Estas plantillas sirven para varias funcionalidades al definir la estructura de la documentación. Están configuradas como markdown interpolado, que admite cualquier expresión de markdown válida. Puede reorganizar las variables, marcadas por {VARIABLE_NAME}, a lo largo del documento para personalizar el diseño según sus preferencias.',
  },
  labels: {
    activity: () => 'Actividad',
    activities: () => 'Actividades',
    activitiesDefaults: () => 'Restaurar valores predeterminados',
    activitiesDistribution: () => 'Distribución de actividades',
    activitiesFix: () => 'Corregir actividades',
    activitiesProjectManagement: () => 'Gestión de Proyectos',
    activitiesSummary: () => 'Resumen de actividades',
    activitiesTotal: () => 'Total (%)',
    activitiesTotalExplain: () =>
      'Cada actividad se asigna entre los roles existentes y los porcentajes asignados a cada rol deben sumar 100%. En caso contrario, la columna cambiará su color a rojo para indicar que la suma es incorrecta.',
    activitiesSurveyAndScope: () => 'Encuesta y Alcance',
    activitiesPrototype: () => 'Prototipo',
    activitiesPrototypeValidation: () => 'Validación de prototipo',
    activitiesFunctionalImplementation: () => 'Implementación funcional',
    activitiesTechnicalImplementation: () => 'Implementación técnica',
    activitiesTesting: () => 'Pruebas',
    activitiesFinalAdjustments: () => 'Ajustes finales',
    activitiesDocumentation: () => 'Documentación',
    activitiesTraining: () => 'Formación',
    activitiesInstallation: () => 'Instalación',
    activitiesDeployment: () => 'Despliegue',
    add: () => 'Agregar',
    addItem: (itemType: string) => parseTOKS(`Agregar <TOK1>`, itemType),
    all: () => 'Todos',
    alteration: () => 'Alteración',
    applyToCurrentProject: () => 'Agregar al proyecto',
    attention: () => 'Atención',
    attribute: () => 'Atributo',
    attributes: () => 'Atributos',
    augmentForm: () => 'Ampliar formulario',
    augmentProcess: () => 'Ampliar proceso',
    augmentPrompt: () => 'Mejorar prompt',
    binary: () => 'Vista previa',
    bold: () => 'Negrita',
    bookSheets: () => 'Hojas de libro',
    both: () => 'Ambos',
    businessClass: () => 'Clase de negocio',
    businessClasses: () => 'Clases de negocio',
    button: () => 'Botón',
    byAttType: () => 'Por tipo de atributo',
    byFieldType: () => 'Por tipo de campo',
    cancel: () => 'Cancelar',
    cancellation: () => 'Cancelación',
    cannotAggregate: () => 'No se puede agregar este elemento',
    category: () => 'Categoría',
    caution: () => 'Precaución',
    chat: () => 'Chat',
    checkbox: () => 'Casilla de verificación',
    chooseExistingElement: () => 'Elegir elemento existente',
    codeBlock: () => 'Bloque de código',
    collection: () => 'Colección',
    combo: () => 'Combobox',
    combobox: () => 'Combobox',
    complexity: () => 'Complejidad',
    complexityLow: () => 'Baja',
    complexityMid: () => 'Media',
    complexityHigh: () => 'Alta',
    complexityHours: () => 'Horas',
    complexityCustom: () => 'Personalizado',
    complexityFactors: () => 'Factores de complejidad',
    complexityFactorsAreas: () => 'Áreas involucradas',
    complexityFactorsAreasTooltip: () =>
      'El número de áreas funcionales, oficinas, grupos, etc. que utilizan el sistema puede afectar potencialmente la complejidad general del proyecto hasta en un 20%.',
    complexityFactorsCounterpart: () => 'Contraparte capacitada',
    complexityFactorsCounterpartTooltip: () =>
      'La contraparte puede realizar una parte significativa del trabajo u ofrecer poco apoyo. Podrían tener mucha experiencia en procesos, cambio organizacional o reingeniería o no tener ninguna experiencia.',
    complexityFactorsIntegration: () => 'Integración',
    complexityFactorsIntegrationTooltip: () =>
      'La integración entre procesos generalmente introduce complejidad adicional al proyecto.',
    complexityFactorsKpis: () => 'Kpis',
    complexityFactorsKpisTooltip: () =>
      'Los indicadores aumentan la complejidad del sistema. Un proyecto con alta complejidad en indicadores puede elevar la complejidad general hasta en un 20%.',
    complexityFactorsOrganizationalMaturity: () => 'Madurez organizacional',
    complexityFactorsOrganizationalMaturityTooltip: () =>
      'Un factor que puede afectar la complejidad del sistema es el nivel de conflicto dentro de la organización respecto al proyecto y su capacidad para ser flexible y adaptarse a los cambios.',
    complexityFactorsRate: () => 'Tasa',
    complexityFactorsTeamExperience: () => 'Experiencia del equipo',
    complexityFactorsTeamExperienceTooltip: () =>
      'Un factor que puede afectar la productividad del desarrollo del sistema es el nivel de experiencia del equipo de consultoría que trabaja en el proyecto. Es recomendable contar con profesionales experimentados en implementaciones de APIA en roles como gestión de proyectos, consultoría funcional y diseño y programación de clases de negocio. La experiencia extensa se refiere a más de 2 años, mientras que la experiencia razonable indica más de 9 meses.',
    complexityFactorsTecFunRelation: () => 'Relación Tec/Fun',
    complexityFactorsTecFunRelationTooltip: () =>
      'El escenario ideal es mantener la relación técnica/funcional cercana al 30% para evitar una complejidad técnica significativa, aunque esto no siempre es manejable y depende de la naturaleza del proyecto. Nunca debe exceder el 50% en BPMS y rara vez se encuentra por debajo del 30%.',
    complexityFactorsUsers: () => 'Cantidad de usuarios',
    complexityFactorsUsersTooltip: () =>
      'La cantidad de usuarios que utilizan el sistema puede afectar potencialmente la complejidad general del proyecto hasta en un 20%',
    configuration: () => 'Configuración',
    convertToBusinessClass: () => 'Convertir a clase de negocio',
    clear: () => 'Limpiar',
    closed: () => 'Cerrado',
    companyIcon: () => 'Ícono de la empresa',
    companyName: () => 'Nombre de la empresa',
    condition: () => 'Condición',
    content: () => 'Contenido',
    copy: () => 'Copiar',
    copyItem: (name: string = '', type: string = '') =>
      parseTOKS('Copiar <TOK1> <TOK2>', type, name),
    copyProject: () => 'Copiar proyecto',
    cost: () => 'Costo',
    costInDays: () => 'En días',
    costInMonths: () => 'En meses',
    costByHour: () => 'Por hora',
    costByProfile: () => 'Por perfil',
    costsHtml: () => 'Costos en HTML',
    count: () => 'Contar',
    createFunctionality: () => 'Crear funcionalidad',
    creation: () => 'Creación',
    cube: () => 'Cubo',
    custom: () => 'Personalizado',
    dashboard: () => 'Tablero',
    dashboardPanel: () => 'Panel',
    dashboardPanels: () => 'Paneles',
    dashboards: () => 'Tableros',
    dataOriginEntities: () => 'Entidades de origen de datos',
    dataType: () => 'Tipo de dato',
    date: () => 'Fecha',
    definition: () => 'Definición',
    delete: () => 'Eliminar',
    dependencies: () => 'Dependencias',
    description: () => 'Descripción',
    details: () => 'Detalles',
    deliverables: () => 'Entregables',
    deliverablesAlreadyExists: () => 'Ya existe un entregable con ese nombre',
    deliverablesEdit: () => 'Editar entregable',
    deliverablesNew: () => 'Nuevo entregable',
    deselectAll: () => 'Deseleccionar todo',
    didNotAugmentPrompt: () =>
      'Estás a punto de iniciar una generación estricta, pero aún no aumentaste el prompt. Esto podría no ser lo que desea, ¿está seguro de querer continuar?',
    document: () => 'Documento',
    documents: () => 'Documentos',
    documentsForProcess: (proName: string) =>
      `Documentos del proceso <div>${proName}</div>`,
    downloadFormat: () => 'Formato de descarga',
    dshDefault: () => 'Tablero predeterminado',
    dshMobileDefault: () => 'Tablero móvil predeterminado',
    editable: () => 'Editable',
    editItemsTemplate: () => 'Editar plantilla de ítems',
    editSpecsTemplate: () => 'Editar plantilla de especificaciones',
    effortByItem: () => 'Esfuerzo estimado por tipo de ítem',
    enabled: () => 'Habilitado',
    enableLocalCardinality: () => 'Habilitar cardinalidad local',
    entCreModalTitle: (entityName: string, entityTitle: string) =>
      parseTOKS(
        `Configuración de importación de entidad <TOK1> (<TOK2>)`,
        entityTitle,
        entityName,
      ),
    entCreTitle: (entityName: string, entityTitle: string) =>
      parseTOKS(
        'Configuración de importación para <TOK1> (<TOK2>)',
        entityTitle,
        entityName,
      ),
    entCreAdmType: () => 'Tipo de administración',
    entCreAltPro: () => 'Proceso de alteración',
    entCreChkCreation: () => 'Crear entidad',
    entCreComboAtt: () => 'Atributo para mostrar en combo',
    entCreCreModal: () => 'Crear modal',
    entCreEntName: () => 'Nombre de la entidad',
    entCreEntPrefix: () => 'Prefijo de la entidad',
    entCreCrePro: () => 'Proceso de creación',
    entCreFrm: () => 'Crear formulario',
    entCreIgnorePK: () => 'Ignorar claves primarias',
    entCreIsCod: () => 'Entidad de carga de código',
    entCreModalRetAtt: () => 'Atributo devuelto por modal',
    entCrePoolForProcesses: () => 'Grupo para procesos',
    entCreQry: () => 'Crear consulta',
    entities: () => 'Entidades',
    entity: () => 'Entidad',
    entityInstance: () => 'Instancia',
    entityInstances: () => 'Instancias',
    entityInstancesCount: (n: number) => `${n} instancias`,
    entityInstanceAttribute: () => 'Atributo de instancia de entidad',
    event: () => 'Evento',
    events: () => 'Eventos',
    excel: () => 'Excel',
    executable: () => 'Ejecutable',
    exportApiaObject: (itemName: string) =>
      parseTOKS(`Crear en Apia: "<TOK1>"`, itemName),
    extended: () => 'Modo extendido',
    externalInterface: () => 'Interfaz externa',
    externalInterfaces: () => 'Interfaces externas',
    field: () => 'Campo',
    fields: () => 'Campos',
    fieldVisualization: () => 'Visualización',
    file: () => 'Archivo',
    findSimilarities: () => 'Encontrar similitudes',
    foreignAttribute: () => 'Atributo extranjero',
    foreignCardinality: () => 'Cardinalidad extranjera',
    foreignEntity: () => 'Entidad extranjera',
    foreignKey: () => 'Clave extranjera',
    foreignKeys: () => 'Claves extranjeras',
    form: () => 'Formulario',
    formField: () => 'Campo de formulario',
    formFields: () => 'Campos de formulario',
    formFromImage: () => 'Desde imagen',
    formGenerateSuccess: () => 'Formulario generado exitosamente',
    forms: () => 'Formularios',
    showContextForms: () => 'Ver formularios de contexto',
    free: () => 'Libre',
    fromImage: () => 'Desde imagen',
    functionality: () => 'Funcionalidad',
    generate: () => 'Generar',
    generateAdditionalData: () => 'Generar datos adicionales',
    generateAdditionalDataPreloaded: () =>
      'Generar datos adicionales (precargado)',
    generateBoth: () => 'Generar ambos',
    generateEntity: () => 'Generar entidad',
    generateForm: () => 'Generar formulario',
    generateForms: () => 'Generar formularios',
    generateImage: () => 'Generar imagen',
    generateInstances: () => 'Generar instancias',
    generateObject: () => 'Generar...',
    generateTasks: () => 'Generar tareas',
    generateProcess: () => 'Generar proceso',
    generateProcessFrom: (what: string) =>
      parseTOKS(`Generar proceso desde <TOK1>`, what),
    generateWithStrictAttributes: () => 'Estricto en los atributos',
    generateWithStrictFields: () => 'Estricto en los campos',
    generateWithStrictTasks: () => 'Estricto en las tareas',
    getTabular: () => 'Obtener vista estructurada',
    goal: () => 'Objetivo',
    goBackToTask: (taskName: string) => `Volver a ${taskName}`,
    goForwardToTask: (taskName: string) => `Ir a ${taskName}`,
    gotoEndTask: () => 'Puede ir al final',
    gotoWhichTasks: () => 'A qué tareas va',
    gotoFlowConfiguration: () => 'Configuración del flujo',
    gotoTasksOrder: () => 'Orden de las tareas',
    grid: () => 'Tabla',
    height: () => 'Altura',
    hidden: () => 'Oculto',
    hideFunctionality: () => 'Ocultar funcionalidad',
    hours: () => 'Horas',
    html: () => 'Html',
    htmlCode: () => 'Código HTML',
    http: () => 'Http',
    iframe: () => 'Iframe',
    image: () => 'Imagen',
    imageDescription: () => 'Descripción de la imagen',
    images: () => 'Imágenes',
    importApiaObject: () => 'Importar desde Apia',
    input: () => 'Entrada',
    instance: () => 'Instancia',
    instances: () => 'Instancias',
    internetSearch: () => 'Búsqueda en internet',
    introduceFormDescription: () => 'Introducir descripción del formulario',
    invisible: () => 'Invisible',
    isStartTask: () => 'Tarea inicial',
    items: () => 'Ítems',
    itemsSummary: () => 'Resumen de elementos',
    kpi: () => 'KPI',
    kpiMaxValue: () => 'Valor máximo de KPI',
    kpiMinValue: () => 'Valor mínimo de KPI',
    kpiMultiplier: () => 'Multiplicador de KPI',
    kpiMultiThousanth: () => 'Milésimo',
    kpiMultiHundredth: () => 'Centésimo',
    kpiMultiTenth: () => 'Décimo',
    kpiMultiOne: () => 'Uno',
    kpiMultiTen: () => 'Diez',
    kpiMultiHundred: () => 'Cien',
    kpiMultiThousand: () => 'Mil',
    kpiMultiTenthousand: () => 'Diez mil',
    kpiType: () => 'Tipo de KPI',
    kpiPeriodicityDaily: () => 'Diario',
    kpiPeriodicityMonthly: () => 'Mensual',
    kpiPeriodicityWeekly: () => 'Semanal',
    kpiPeriodicityYearly: () => 'Anual',
    kpiTypeCounter: () => 'Contador',
    kpiTypeOxford: () => 'Oxford',
    kpiTypeRing: () => 'Anillo',
    kpiTypeScale: () => 'Escala',
    kpiTypeSpeedometer: () => 'Velocímetro',
    kpiTypeThermometer: () => 'Termómetro',
    kpiTypeTrafficLight: () => 'Semáforo',
    kpis: () => 'KPIs',
    java: () => 'Java',
    javascript: () => 'Javascript',
    jsp: () => 'JSP',
    keepExistingDefinition: () => 'Mantener definición existente',
    label: () => 'Etiqueta',
    landingPage: () => 'Página de inicio',
    landingPageDefault: () => 'Página de inicio predeterminada',
    length: () => 'Longitud',
    localAttribute: () => 'Atributo local',
    localCardinality: () => 'Cardinalidad local',
    mask: () => 'Máscara',
    mergeItems: (itemType: string) => parseTOKS(`Fusionar <TOK1>`, itemType),
    metadata: () => 'Metadatos',
    model: () => 'Modelo',

    name: () => 'Nombre',
    naming: () => 'Denominación',
    newEntityTitle: (entityTitle: string) =>
      parseTOKS(`Nueva instancia (<TOK1>)`, entityTitle),
    noImage: () => 'Sin imagen',
    noItemsToAutofill: () => 'No hay elementos para autocompletar',
    noDocsSelected: () => 'Se debe seleccionar al menos un documento',
    none: () => 'Ninguno',
    nonStrictGenerationWarning: () =>
      'Estás a punto de iniciar una generación no estricta. Esto podría no ser lo que desea. ¿está seguro de querer continuar?',
    nothingToMerge: () =>
      'No hay elementos similares en la colección seleccionada',
    noSheetsSelected: () => 'Se debe seleccionar al menos una hoja',
    notIncluded: () => 'No incluido',
    noTitleOnAddedElement: (type: string, name: string) =>
      parseTOKS(
        `El elemento "<TOK1>" del tipo "<TOK2>" debe tener un título`,
        name,
        type,
      ),
    number: () => 'Número',
    objects: () => 'Objetos',
    objectsCount: (count: number | string) =>
      parseTOKS(`<TOK1> elementos`, count),
    objectsNames: () => 'Nombres de objetos (separados por comas)',
    objectType: () => 'Tipo de objeto',
    objectDependencies: () => 'Ocurrencias del ítem:',
    omit: () => 'Omitir',
    omitEverything: () => 'Omitir todo',
    omittedItems: () => 'Ítems omitidos',
    openCube: () => 'Abrir cubo',
    openQuery: () => 'Abrir consulta',
    openWidgetDefinition: () => 'Abrir definición de widget',
    options: () => 'Opciones',
    other: () => 'Otro',
    outcome: () => 'Resultado',
    outcomes: () => 'Resultados',
    palette: () => 'Paleta',
    paletteBackgroundDefault: () => 'Fondo predeterminado',
    paletteBackgroundPaper: () => 'Papel de fondo',
    palettePrimaryMain: () => 'Primario',
    palettePrimaryContrastText: () => 'Texto contrastante primario',
    paletteSecondaryMain: () => 'Secundario',
    paletteSecondaryContrastText: () => 'Texto contrastante secundario',
    paletteTextPrimary: () => 'Texto normal',
    paletteTextTitle: () => 'Títulos',
    panel: () => 'Panel',
    panels: () => 'Paneles',
    parameters: () => 'Parámetros',
    paste: () => 'Pegar',
    path: () => 'Ruta',
    performer: () => 'Rol',
    performers: () => 'Roles',
    periodicity: () => 'Periodicidad',
    pool: () => 'Grupo',
    pools: () => 'Grupos',
    position: () => 'Posición',
    prefix: () => 'Prefijo',
    prefixes: () => 'Prefijos',
    pricing: () => 'Precios',
    pricingIncludedObjects: () => 'Objetos incluidos',
    prompt: () => 'Mensaje',
    process: () => 'Proceso',
    processes: () => 'Procesos',
    processEntity: (proName: string) =>
      `Entidad principal del proceso <div>${proName}</div>`,
    processTask: () => 'Tarea del proceso',
    processTasks: () => 'Tareas del proceso',
    profile: () => 'Perfil',
    profiles: () => 'Perfiles',
    profileTotalHours: () => 'Total de horas del perfil',
    project: () => 'Proyecto',
    projectDetails: (projectName: string) => parseTOKS('<TOK1>', projectName),
    projectDocumentation: (projectName: string) =>
      parseTOKS(`Documentación de <TOK1>`, projectName),
    projectEstimate: (projectName: string) =>
      parseTOKS(`Estimación para el proyecto '<TOK1>'`, projectName),
    projectIcon: () => 'Ícono del proyecto',
    projectName: () => 'Nombre del proyecto',
    projectPlan: () => 'Plan de proyecto',
    properties: () => 'Propiedades',
    proposal: () => 'Propuesta',
    proposalCost: () => 'Costo de la propuesta',
    proposalEffort: () => 'Esfuerzo de la propuesta',
    proposalScope: () => 'Alcance de la propuesta',
    projectTypes: () => 'Tipos de proyecto',
    query: () => 'Consulta',
    queries: () => 'Consultas',
    radio: () => 'Radio',
    readonly: () => 'Solo lectura',
    react: () => 'React',
    reference: () => 'Referencia',
    references: () => 'Referencias',
    refresh: () => 'Actualizar',
    refreshOnFocus: () => 'Actualizar al recibir el foco',
    regenerateEntity: () => 'Regenerar entidad',
    regenerateForm: () => 'Regenerar formulario',
    regenerateProcess: () => 'Regenerar proceso',
    regexp: () => 'Expresión regular',
    relatedProcess: () => 'Proceso relacionado',
    relatedProcesses: () => 'Procesos relacionados',
    relatedProcessesForProcess: (proName: string) =>
      `Procesos relacionados con <div>${proName}</div>`,
    removable: () => 'Removible',
    remove: () => 'Eliminar',
    removeElementTitle: (elementType: string, title: string) =>
      parseTOKS(`[<TOK1>] <TOK2>`, elementType, title),
    removeItem: (elementType: string, itemTitle: string) =>
      parseTOKS(`Eliminar <TOK1> <TOK2>`, elementType, itemTitle),
    removeSources: () => 'Eliminar fuentes',
    replaceExistingPrompt: () => 'Reemplazar prompt actual',
    replaceWithNewElement: () => 'Reemplazar con nueva definición',
    removeStateFromEntityWarning: () =>
      'Eliminar este estado de la entidad también eliminará el estado de todos los procesos que lo contengan.',
    renameExistingProject: () => 'Renombrar elemento existente',
    renameConflictingElement: () => 'Renombrar elemento en conflicto',
    required: () => 'Requerido',
    rest: () => 'Rest',
    result: () => 'Resultado',
    role: () => 'Rol',
    roles: () => 'Roles',
    rolesAndActivities: () => 'Roles y actividades',
    rolesBlackBoxTester: () => 'Tester de caja negra',
    rolesExternalFunctionalConsultant: () => 'Consultor funcional externo',
    rolesFunctionalConsultant: () => 'Consultor funcional',
    rolesProjectManager: () => 'Gestor de proyectos',
    rolesSalesExecutive: () => 'Ejecutivo de ventas',
    rolesServicesDirector: () => 'Director de servicios',
    rolesTechnicalSolutionArchitect: () => 'Arquitecto de soluciones técnicas',
    rolesTechnicalComponentsProgrammer: () =>
      'Programador de componentes técnicos',
    routine: () => 'Rutina',
    rule: () => 'Regla',
    rules: () => 'Reglas de negocio',
    rulesForProcess: (proName: string) =>
      `Reglas de negocio del proceso <div>${proName}</div>`,
    schedule: () => 'Agenda',
    scheduleExcelTitle: () =>
      'Agenda de actividades, funciones, entregables y procesos',
    scheduleCollaboratorsAverage: () => 'Promedio de colaboradores necesarios',
    scheduleCollaboratorsMax: () => 'Máximo de colaboradores necesarios',
    scheduleCollaboratorsMin: () => 'Mínimo de colaboradores necesarios',
    scheduleFromTo: (from: TId, to: TId) =>
      parseTOKS(`Del día <TOK1> al día <TOK2>`, from, to),
    scheduleExplain: () =>
      '<p>Especifica la cantidad de días laborables que planeas dedicar a este proyecto y detalla la participación de los colaboradores a lo largo del tiempo. Esto determinará directamente el número de colaboradores necesarios para cumplir con el plazo deseado.</p><p>Normalmente, un proyecto común tiene un plazo de sesenta días laborables.</p>',
    scheduleExplainPricing: () =>
      'Ingresa el precio por hora de cada rol para calcular el costo total.',
    scheduleHoursCount: (count: TId) => parseTOKS(`<TOK1>hs`, count),
    schedulePeopleCount: (count: TId) => parseTOKS(`<TOK1> personas`, count),
    schedulePeopleCountMin: (count: TId) => parseTOKS(`<TOK1>p`, count),
    schedulePeopleMax: (count: TId, from: TId, to: TId) =>
      parseTOKS(`<TOK1> personas (Día <TOK2> al <TOK3>)`, count, from, to),
    schedulePeriod: () => 'Período',
    scheduleTitleCollaboratorsDetails: () =>
      'Detalles semanales de los colaboradores',
    scheduleTitleCollaboratorsSummary: () => 'Resumen de colaboradores',
    scheduleTitleDeliverablesSchedule: () => 'Plazo para entregables',
    scheduleTitleObjectsSchedule: () => 'Plazo para objetos',
    scheduleTitleDiagram: () => 'Agenda de distribución de actividades',
    scheduleTitlePricing: () => 'Precios',
    scope: () => 'Alcance',
    search: () => 'Buscar',
    section: () => 'Sección',
    sections: () => 'Secciones',
    selectAll: () => 'Seleccionar todo',
    selectWeek: () => 'Seleccionar semana',
    showComments: () => 'Mostrar comentarios',
    showHistory: () => 'Mostrar historial',
    showInformation: () => 'Mostrar información',
    showRefresh: () => 'Mostrar actualización',
    showDetailsForItem: (itemName: string) =>
      parseTOKS(`Mostrar detalles de <TOK1>`, itemName),
    showInSiteMap: () => 'Mostrar en el mapa del sitio',
    showValue: () => 'Mostrar valor',
    similaritiesFound: () => 'Similitudes encontradas',
    size: () => 'Tamaño',
    soa: () => 'SOA',
    somethingWrongGenForms: () =>
      'Hubo un error al generar el formulario, por favor intenta de nuevo.',
    sourceItems: () => 'Elementos de origen',
    sortProcessTasks: (processTitle: string) =>
      parseTOKS(`Ordenar tareas de '<TOK1>'`, processTitle),
    specsHTML: () => 'Especificaciones HTML',
    srcName: () => 'Nombre de origen',
    state: () => 'Estado',
    states: () => 'Estados',
    statesForProcess: (proName: string) =>
      `Estados del proceso <div>${proName}</div>`,
    status: () => 'Estatus',
    string: () => 'String',
    structured: () => 'Estructurado',
    structuredExamples: () => 'Ejemplos estructurados',
    suffixes: () => 'Sufijos',
    summary: () => 'Resumen',
    summaryAttributesHours: () => 'Atributos',
    summaryBusinessClassesHours: () => 'Clases de negocio',
    summaryDocumentsHours: () => 'Documentos',
    summaryEntitiesHours: () => 'Entidades',
    summaryExternalInterfacesHours: () => 'Interfaces externas',
    summaryFormsHours: () => 'Formularios',
    summaryKpisHours: () => 'KPIs',
    summaryFixedCost: () => 'Costo fijo',
    summaryVariableCost: () => 'Costo variable',
    summaryProcessHours: () => 'Proceso',
    summarySubtotal: () => 'Subtotal',
    summaryFactors: () => 'Factores de complejidad',
    summaryTotal: () => 'Total',
    supervisor: () => 'Supervisor',
    tab: () => 'Mostrar como pestaña',
    targetItem: () => 'Elemento objetivo',
    task: () => 'Tarea',
    tasks: () => 'Tareas',
    tasksForProcess: (proName: string) =>
      `Tareas del proceso <div>${proName}</div>`,
    taskForProcess: (tskName: string, proName: string) =>
      `Tarea ${tskName} <div>del proceso ${proName}</div>`,
    template: () => 'Plantilla',
    templates: () => 'Plantillas',
    textarea: () => 'Área de texto',
    theme: () => 'Tema',
    threshold: () => 'Umbral',
    title: () => 'Título',
    tooMuchFields: () =>
      'El prompt actual parece tener demasiados campos. Esto podría implicar un alto costo, ¿desea continuar?',
    semiStructuredPromptRequired: () =>
      'No se puede generar el proceso hasta que exista un prompt semiestructurado válido. Genera o ajusta el prompt central desde el chat antes de continuar.',
    totalHours: () => 'Horas totales',
    transcribeAudio: () => 'Transcribir audio',
    trigger: () => 'Disparador',
    triggers: () => 'Disparadores',
    type: () => 'Tipo',
    unnamedProject: () => 'Projecto sin nombre',
    uploadFromDisk: () => 'Cargar desde disco',
    useCase: () => 'Caso de uso',
    useCases: () => 'Casos de uso',
    useCasesForProcess: (proName: string) =>
      `Casos de uso del proceso <div>${proName}</div>`,
    user: () => 'Usuario',
    users: () => 'Usuarios',
    value: () => 'Valor',
    view: () => 'Vista',
    visible: () => 'Visible',
    week: (no?: number) => parseTOKS(`Semana nro. <TOK1>`, no ?? ''),
    weekNo: () => 'Semana nro.',
    weekSummary: () => 'Resumen de la semana',
    widget: () => 'Widget',
    widgetPanel: () => 'Panel de widgets',
    widgets: () => 'Widgets',
    whyShouldFix: () =>
      'Este botón ajusta los tiempos introducidos para tener un total de 1 por cada función en cada actividad',
  },
  questions: {
    augmentProcessIntroduction: () =>
      `Por favor, a continuación introduzca el mensaje de personalización del aumento`,
    augmentProcessPrompt: (process: {
      name: string;
      title?: string;
      description?: string;
    }) =>
      parseTOKS(
        `<TOK1><TOK2>`,
        process.title ? `${process.title}\n\n` : '',
        process.description
          ? `Descripción del proceso: ${process.description}`
          : '',
      ),
    cannotReadFile: () => 'No se puede leer el archivo como proyecto',
    cannotRemoveBecauseOfDependencies: () =>
      `Eliminar este elemento también eliminará las siguientes rutas de dependencia. ¿Desea continuar?:`,
    changesWillBeApplied: () =>
      'Los elementos del proyecto se crearán en el ambiente actual. Esta acción es irreversible. ¿Desea continuar?',
    changesWillBeLost: () => 'Se perderán los cambios',
    chooseExistingElement: (elementType: string) =>
      parseTOKS(`Elija <TOK1>`, elementType),
    cloneNewName: () => `Por favor, introduzca el nombre del nuevo elemento`,
    couldNotGetProcess: () => 'Algo salió mal al obtener el proceso',
    createItem: (itemName: string) => parseTOKS(`Nuevo <TOK1>`, itemName),
    doTheSameForEvery: (itemType: string) =>
      parseTOKS(`Hacer lo mismo para cada <TOK1>`, itemType),
    doThemSameForEverything: () => 'Hacer lo mismo para todo',
    doYouWishToContinue: () => '¿Desea continuar?',
    editForeignKeyConsequences: () =>
      'Editar una clave foránea eliminará todas sus instancias. ¿Desea continuar?',
    itemAlreadyAdded: (itemName: string) =>
      parseTOKS(
        `El elemento llamado "<TOK1>" ya ha sido agregado al proyecto`,
        itemName,
      ),
    makeNewForeignKeyConsequences: () =>
      'Agregar una nueva clave foránea eliminará todas sus instancias. ¿Desea continuar?',
    removeInstancesAttributesAsWell: () =>
      'Eliminar este atributo de la entidad también eliminará los valores de los atributos de las instancias. ¿Desea continuar?',
    removeOrphanElements: () =>
      'Esta acción eliminará estos elementos huérfanos:',
    removeTheElement: () => `Esta acción eliminará el elemento`,
    removeWidgetConfirm: () =>
      '¿Está seguro de que desea eliminar este widget? También se eliminarán los paneles autogenerados.',
    renameAlreadyUsedName: () =>
      `El nombre elegido ya está en uso, por favor elija otro`,
    thereIsNothingToRemove: () => 'No hay nada para eliminar',
    updateFieldClone: () => 'Clonar y reemplazar',
    updateFieldConfirm: () => 'Sobrescribir',
    updateField: ({ TOK1 }: { TOK1: string }) =>
      parseTOKS(`¿Está seguro de que desea actualizar <TOK1>?`, TOK1),
    updateAffectedFields: () => 'Este cambio afectará las siguientes rutas:',
  },
  tools: {
    addProject: 'Agregar definición al proyecto actual',
    advanced: 'Avanzado',
    apply: 'Aplicar',
    autofill: 'Autocompletar',
    basic: 'Básico',
    close: 'Cerrar',
    commandPalette: 'Paleta de comandos (Ctrl + Q)',
    configuration: 'Configuración',
    collapse: 'Contraer todos los nodos',
    distance: 'Buscar objetos similares',
    document: 'Imprimir documentación',
    export: 'Exportar',
    import: 'Importar',
    intermediate: 'Intermedio',
    merge: 'Fusionar',
    new: 'Nuevo',
    notes: 'Notas (Ctrl+Shift+E)',
    open: 'Abrir',
    projectPlan: 'Plan de proyecto',
    redo: 'Rehacer',
    save: 'Guardar',
    saveConfiguration: 'Guardar configuración',
    saveDeliverables: 'Guardar entregables',
    saveNaming: 'Guardar nomenclatura',
    saveParameters: 'Guardar parámetros',
    saveRolesAndActivities: 'Guardar roles y actividades',
    saveTemplates: 'Guardar plantillas',
    sort: 'Ordenar',
    sortByNameAsc: 'Ordenar por nombre ascendente',
    sortByNameDesc: 'Ordenar por nombre descendente',
    sortByTitleAsc: 'Ordenar por título ascendente',
    sortByTitleDesc: 'Ordenar por título descendente',
    undo: 'Deshacer',
    vacuum: 'Limpieza',
  },
});
